import type { CommunicationRecord } from "@entities";
import { linko } from "@shared/linko";
import {
  formatPartnerOrgActivityDate,
  formatPartnerOrgFollowUpDate,
  lastLoginFromManagers,
} from "@shared/helpers/partner-organization-activity";

const CATALOG_STATS_CHUNK_SIZE = 200;

export type PartnerOrganizationActivity = {
  lastLoginAt: Date | null;
  lastNeedAt: Date | null;
  lastCommunicationAt: Date | null;
  lastCommunications: CommunicationRecord[];
};

export type PartnerOrganizationCatalogStatsMaps = {
  activity: Record<string, PartnerOrganizationActivity>;
  openLeavesCount: Record<string, number>;
};

export {
  formatPartnerOrgActivityDate,
  formatPartnerOrgFollowUpDate,
  lastLoginFromManagers,
};

type PartnerOrganizationCatalogLastCommunication = {
  id: string;
  createdAt: Date | string;
  title: string;
  description: string;
};

type PartnerOrganizationCatalogStatsRow = {
  partnerOrganizationId: string;
  openLeavesCount: number;
  lastLoginAt: Date | string | null;
  lastNeedAt: Date | string | null;
  lastCommunicationAt: Date | string | null;
  lastCommunication: PartnerOrganizationCatalogLastCommunication | null;
};

type PartnerOrganizationCatalogStatsResult = {
  organizations: PartnerOrganizationCatalogStatsRow[];
};

type StatisticsCatalogQueries = {
  getPartnerOrganizationCatalogStats: (body: {
    partnerOrganizationIds: string[];
  }) => Promise<{ data: PartnerOrganizationCatalogStatsResult }>;
};

function asDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function emptyActivity(): PartnerOrganizationActivity {
  return {
    lastLoginAt: null,
    lastNeedAt: null,
    lastCommunicationAt: null,
    lastCommunications: [],
  };
}

function toCommunicationRecord(
  lastCommunication: PartnerOrganizationCatalogLastCommunication,
): CommunicationRecord {
  const createdAt = asDate(lastCommunication.createdAt) ?? new Date();
  return {
    id: lastCommunication.id,
    createdAt,
    title: lastCommunication.title,
    description: lastCommunication.description,
  } as CommunicationRecord;
}

function emptyMaps(orgIds: string[]): PartnerOrganizationCatalogStatsMaps {
  const activity: Record<string, PartnerOrganizationActivity> = {};
  const openLeavesCount: Record<string, number> = {};
  for (const id of orgIds) {
    activity[id] = emptyActivity();
    openLeavesCount[id] = 0;
  }
  return { activity, openLeavesCount };
}

export async function fetchPartnerOrganizationCatalogStats(
  orgIds: string[],
): Promise<PartnerOrganizationCatalogStatsMaps> {
  const uniqueIds = [...new Set(orgIds.filter(Boolean))];
  const result = emptyMaps(uniqueIds);
  if (uniqueIds.length === 0) return result;

  const queries = linko.statistics.queries as unknown as StatisticsCatalogQueries;
  const chunks: string[][] = [];
  for (let i = 0; i < uniqueIds.length; i += CATALOG_STATS_CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(i, i + CATALOG_STATS_CHUNK_SIZE));
  }
  const pages = await Promise.all(
    chunks.map((chunk) =>
      queries.getPartnerOrganizationCatalogStats({
        partnerOrganizationIds: chunk,
      }),
    ),
  );
  for (const { data } of pages) {
    for (const row of data.organizations ?? []) {
      const orgId = row.partnerOrganizationId;
      if (!orgId || !result.activity[orgId]) continue;
      result.openLeavesCount[orgId] = row.openLeavesCount ?? 0;
      result.activity[orgId] = {
        lastLoginAt: asDate(row.lastLoginAt),
        lastNeedAt: asDate(row.lastNeedAt),
        lastCommunicationAt: asDate(row.lastCommunicationAt),
        lastCommunications: row.lastCommunication
          ? [toCommunicationRecord(row.lastCommunication)]
          : [],
      };
    }
  }

  return result;
}

export async function fetchPartnerOrganizationActivity(
  orgIds: string[],
): Promise<Record<string, PartnerOrganizationActivity>> {
  const { activity } = await fetchPartnerOrganizationCatalogStats(orgIds);
  return activity;
}
